import { useState } from 'react';
import {
  Share2,
  Plus,
  Calendar,
  Clock,
  FileText,
  Users,
  Mail,
  Download,
  Trash2,
  Edit,
  MoreHorizontal,
  Search,
  Filter,
  Star,
  StarOff,
  Play,
  Pause,
  Loader2,
  X,
  UserPlus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useSavedReports,
  useCreateSavedReport,
  useUpdateSavedReport,
  useDeleteSavedReport,
  useToggleFavorite,
  useUpsertSchedule,
  useToggleSchedule,
  useRunReport,
  useReportShares,
  useShareReport,
  useRemoveShare,
  formatSchedule,
  type SavedReport,
  type ReportType,
  type ScheduleFrequency,
} from '@/hooks/useSavedReports';
import { useTeam } from '@/hooks/useTeam';
import { useAuth } from '@/hooks/useAuth';

const REPORT_TYPE_CONFIG: Record<ReportType, { label: string; color: string }> = {
  sales: { label: 'Sales', color: 'bg-green-100 text-green-800' },
  inventory: { label: 'Inventory', color: 'bg-blue-100 text-blue-800' },
  register: { label: 'Register', color: 'bg-purple-100 text-purple-800' },
  user: { label: 'User', color: 'bg-amber-100 text-amber-800' },
  payment: { label: 'Payment', color: 'bg-pink-100 text-pink-800' },
  category: { label: 'Category', color: 'bg-teal-100 text-teal-800' },
  gift_card: { label: 'Gift Card', color: 'bg-orange-100 text-orange-800' },
  store_credit: { label: 'Store Credit', color: 'bg-indigo-100 text-indigo-800' },
  adjustment: { label: 'Adjustment', color: 'bg-red-100 text-red-800' },
  custom: { label: 'Custom', color: 'bg-gray-100 text-gray-800' },
};

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function SharedReports() {
  const { profile } = useAuth();
  const { teamMembers } = useTeam();
  
  // Data fetching
  const { data: savedReports = [], isLoading } = useSavedReports();
  
  // Mutations
  const createReport = useCreateSavedReport();
  const updateReport = useUpdateSavedReport();
  const deleteReport = useDeleteSavedReport();
  const toggleFavorite = useToggleFavorite();
  const upsertSchedule = useUpsertSchedule();
  const toggleSchedule = useToggleSchedule();
  const runReport = useRunReport();
  const shareReport = useShareReport();
  const removeShare = useRemoveShare();
  
  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<SavedReport | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [schedulingReport, setSchedulingReport] = useState<SavedReport | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharingReport, setSharingReport] = useState<SavedReport | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingReport, setDeletingReport] = useState<SavedReport | null>(null);
  
  // Form states
  const [reportForm, setReportForm] = useState({
    name: '',
    description: '',
    report_type: 'sales' as ReportType,
    is_public: false,
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'weekly' as ScheduleFrequency,
    day_of_week: 1,
    day_of_month: 1,
    time_of_day: '08:00',
    recipients: '',
    is_active: true,
  });

  // Report shares
  const { data: reportShares = [] } = useReportShares(sharingReport?.id || null);

  // Filter reports
  const filteredReports = savedReports.filter(report => {
    if (typeFilter !== 'all' && report.report_type !== typeFilter) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        report.name.toLowerCase().includes(query) ||
        (report.description || '').toLowerCase().includes(query) ||
        (report.creator ? `${report.creator.first_name} ${report.creator.last_name}`.toLowerCase().includes(query) : false)
      );
    }
    return true;
  });

  // Handlers
  const handleToggleFavorite = async (report: SavedReport) => {
    try {
      await toggleFavorite.mutateAsync({
        reportId: report.id,
        isFavorite: report.is_favorite || false,
      });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to update favorite',
      });
    }
  };

  const handleToggleScheduleActive = async (report: SavedReport) => {
    if (!report.schedule) return;
    try {
      await toggleSchedule.mutateAsync({
        scheduleId: report.schedule.id,
        isActive: !report.schedule.is_active,
      });
      toast(report.schedule.is_active ? 'Schedule paused' : 'Schedule activated');
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to update schedule',
      });
    }
  };

  const handleRunReport = async (report: SavedReport) => {
    try {
      toast.success('Running report...');
      await runReport.mutateAsync(report.id);
      toast.success('Report completed', { description: 'Your report has been generated' });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to run report',
      });
    }
  };

  const handleCreateReport = async () => {
    try {
      await createReport.mutateAsync({
        name: reportForm.name,
        description: reportForm.description || undefined,
        report_type: reportForm.report_type,
        is_public: reportForm.is_public,
      });
      toast.success('Report created successfully');
      setCreateModalOpen(false);
      setReportForm({ name: '', description: '', report_type: 'sales', is_public: false });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to create report',
      });
    }
  };

  const handleUpdateReport = async () => {
    if (!editingReport) return;
    try {
      await updateReport.mutateAsync({
        id: editingReport.id,
        name: reportForm.name,
        description: reportForm.description || undefined,
        report_type: reportForm.report_type,
        is_public: reportForm.is_public,
      });
      toast.success('Report updated successfully');
      setEditingReport(null);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to update report',
      });
    }
  };

  const handleDeleteReport = async () => {
    if (!deletingReport) return;
    try {
      await deleteReport.mutateAsync(deletingReport.id);
      toast.success('Report deleted');
      setDeleteModalOpen(false);
      setDeletingReport(null);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to delete report',
      });
    }
  };

  const handleSaveSchedule = async () => {
    if (!schedulingReport) return;
    try {
      const recipients = scheduleForm.recipients
        .split(',')
        .map(e => e.trim())
        .filter(e => e);

      await upsertSchedule.mutateAsync({
        saved_report_id: schedulingReport.id,
        frequency: scheduleForm.frequency,
        day_of_week: scheduleForm.frequency === 'weekly' ? scheduleForm.day_of_week : undefined,
        day_of_month: scheduleForm.frequency === 'monthly' ? scheduleForm.day_of_month : undefined,
        time_of_day: scheduleForm.time_of_day,
        recipients,
        is_active: scheduleForm.is_active,
      });
      toast.success('Schedule saved');
      setScheduleModalOpen(false);
      setSchedulingReport(null);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to save schedule',
      });
    }
  };

  const handleShareWithUser = async (userId: string) => {
    if (!sharingReport) return;
    try {
      await shareReport.mutateAsync({
        saved_report_id: sharingReport.id,
        shared_with_user_id: userId,
      });
      toast.success('Report shared');
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to share report',
      });
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    if (!sharingReport) return;
    try {
      await removeShare.mutateAsync({ shareId, reportId: sharingReport.id });
      toast.success('Share removed');
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to remove share',
      });
    }
  };

  const openEditModal = (report: SavedReport) => {
    setReportForm({
      name: report.name,
      description: report.description || '',
      report_type: report.report_type,
      is_public: report.is_public,
    });
    setEditingReport(report);
  };

  const openScheduleModal = (report: SavedReport) => {
    if (report.schedule) {
      setScheduleForm({
        frequency: report.schedule.frequency,
        day_of_week: report.schedule.day_of_week || 1,
        day_of_month: report.schedule.day_of_month || 1,
        time_of_day: report.schedule.time_of_day?.slice(0, 5) || '08:00',
        recipients: report.schedule.recipients.join(', '),
        is_active: report.schedule.is_active,
      });
    } else {
      setScheduleForm({
        frequency: 'weekly',
        day_of_week: 1,
        day_of_month: 1,
        time_of_day: '08:00',
        recipients: '',
        is_active: true,
      });
    }
    setSchedulingReport(report);
    setScheduleModalOpen(true);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getReportTypeBadge = (type: ReportType) => {
    const config = REPORT_TYPE_CONFIG[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return (
      <Badge variant="secondary" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Get users not yet shared with
  const availableUsers = teamMembers.filter(
    member => 
      member.id !== profile?.id &&
      !reportShares.some(s => s.shared_with_user_id === member.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Shared Reports</h1>
          <p className="text-sm text-muted-foreground">
            Save, schedule, and share report configurations with your team
          </p>
        </div>
        <Button onClick={() => { setReportForm({ name: '', description: '', report_type: 'sales', is_public: false }); setCreateModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Create Report
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Saved Reports</span>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">{savedReports.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Scheduled</span>
              <Clock className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {savedReports.filter(r => r.schedule?.is_active).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Shared With Me</span>
              <Users className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {savedReports.filter(r => r.created_by !== profile?.id).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Favorites</span>
              <Star className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {savedReports.filter(r => r.is_favorite).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(REPORT_TYPE_CONFIG).map(([value, config]) => (
              <SelectItem key={value} value={value}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Saved Reports</CardTitle>
          <CardDescription>
            Your saved report configurations and scheduled exports
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {savedReports.length === 0 
                  ? 'No saved reports yet. Create your first report to get started.'
                  : 'No reports match your search criteria.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Report Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <button
                        onClick={() => handleToggleFavorite(report)}
                        className="text-muted-foreground hover:text-amber-500"
                        disabled={toggleFavorite.isPending}
                      >
                        {report.is_favorite ? (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        ) : (
                          <StarOff className="h-4 w-4" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{report.name}</span>
                        {report.description && (
                          <p className="text-xs text-muted-foreground">{report.description}</p>
                        )}
                        {report.is_public && (
                          <Badge variant="outline" className="text-xs mt-1">Public</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getReportTypeBadge(report.report_type)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {report.creator 
                        ? `${report.creator.first_name} ${report.creator.last_name}`
                        : 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {report.schedule ? (
                          <>
                            <button
                              onClick={() => handleToggleScheduleActive(report)}
                              className={cn(
                                "p-1 rounded",
                                report.schedule.is_active 
                                  ? "text-green-600 hover:bg-green-50" 
                                  : "text-muted-foreground hover:bg-muted"
                              )}
                              disabled={toggleSchedule.isPending}
                            >
                              {report.schedule.is_active ? (
                                <Play className="h-3 w-3" />
                              ) : (
                                <Pause className="h-3 w-3" />
                              )}
                            </button>
                            <span className={cn(
                              "text-sm",
                              !report.schedule.is_active && "text-muted-foreground"
                            )}>
                              {formatSchedule(report.schedule)}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not scheduled</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(report.schedule?.last_run_at || null)}
                    </TableCell>
                    <TableCell>
                      {report.schedule && report.schedule.recipients.length > 0 ? (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{report.schedule.recipients.length}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleRunReport(report)}
                            disabled={runReport.isPending}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Run Now
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSharingReport(report); setShareModalOpen(true); }}>
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openEditModal(report)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openScheduleModal(report)}>
                            <Calendar className="h-4 w-4 mr-2" />
                            Schedule
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => { setDeletingReport(report); setDeleteModalOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <Clock className="h-8 w-8 text-blue-500 mb-4" />
            <h3 className="font-semibold mb-2">Scheduled Reports</h3>
            <p className="text-sm text-muted-foreground">
              Set up automatic report generation on a daily, weekly, or monthly basis.
              Reports are delivered directly to your inbox.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Users className="h-8 w-8 text-green-500 mb-4" />
            <h3 className="font-semibold mb-2">Team Sharing</h3>
            <p className="text-sm text-muted-foreground">
              Share report configurations with team members. Control who can view,
              edit, or run your saved reports.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Mail className="h-8 w-8 text-purple-500 mb-4" />
            <h3 className="font-semibold mb-2">Email Delivery</h3>
            <p className="text-sm text-muted-foreground">
              Automatically send reports to stakeholders. Configure multiple
              recipients and delivery formats (PDF, CSV, Excel).
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Report Modal */}
      <ResponsiveModal open={createModalOpen || !!editingReport} onOpenChange={(open) => { if (!open) { setCreateModalOpen(false); setEditingReport(null); } }}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingReport ? 'Edit Report' : 'Create Report'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Configure your saved report
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-2">
              <Label>Report Name</Label>
              <Input
                value={reportForm.name}
                onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })}
                placeholder="e.g., Weekly Sales Summary"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={reportForm.description}
                onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select
                value={reportForm.report_type}
                onValueChange={(v) => setReportForm({ ...reportForm, report_type: v as ReportType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REPORT_TYPE_CONFIG).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={reportForm.is_public}
                onCheckedChange={(v) => setReportForm({ ...reportForm, is_public: v })}
              />
              <Label>Make public to all organization members</Label>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => { setCreateModalOpen(false); setEditingReport(null); }}>
              Cancel
            </Button>
            <Button
              onClick={editingReport ? handleUpdateReport : handleCreateReport}
              disabled={!reportForm.name || createReport.isPending || updateReport.isPending}
            >
              {(createReport.isPending || updateReport.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingReport ? 'Update' : 'Create'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Schedule Modal */}
      <ResponsiveModal open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Schedule Report</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Configure automatic report delivery for "{schedulingReport?.name}"
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={scheduleForm.frequency}
                onValueChange={(v) => setScheduleForm({ ...scheduleForm, frequency: v as ScheduleFrequency })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scheduleForm.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select
                  value={String(scheduleForm.day_of_week)}
                  onValueChange={(v) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={String(day.value)}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {scheduleForm.frequency === 'monthly' && (
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <Select
                  value={String(scheduleForm.day_of_month)}
                  onValueChange={(v) => setScheduleForm({ ...scheduleForm, day_of_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={scheduleForm.time_of_day}
                onChange={(e) => setScheduleForm({ ...scheduleForm, time_of_day: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Recipients (comma-separated emails)</Label>
              <Input
                value={scheduleForm.recipients}
                onChange={(e) => setScheduleForm({ ...scheduleForm, recipients: e.target.value })}
                placeholder="email@example.com, another@example.com"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={scheduleForm.is_active}
                onCheckedChange={(v) => setScheduleForm({ ...scheduleForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setScheduleModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchedule}
              disabled={upsertSchedule.isPending}
            >
              {upsertSchedule.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Schedule
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Share Modal */}
      <ResponsiveModal open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Share Report</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Share "{sharingReport?.name}" with team members
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            {/* Current shares */}
            {reportShares.length > 0 && (
              <div className="space-y-2">
                <Label>Shared With</Label>
                <div className="space-y-2">
                  {reportShares.map((share) => (
                    <div key={share.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                          {share.user?.first_name?.[0]}{share.user?.last_name?.[0]}
                        </div>
                        <span className="text-sm">
                          {share.user?.first_name} {share.user?.last_name}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600"
                        onClick={() => handleRemoveShare(share.id)}
                        disabled={removeShare.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add new share */}
            <div className="space-y-2">
              <Label>Add Team Member</Label>
              {availableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  All team members already have access
                </p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {availableUsers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleShareWithUser(member.id)}
                      disabled={shareReport.isPending}
                      className="w-full flex items-center gap-3 p-2 rounded hover:bg-muted transition-colors text-left"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{member.first_name} {member.last_name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                      <UserPlus className="h-4 w-4 ml-auto text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setShareModalOpen(false)}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <ResponsiveModalContent className="max-w-sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Delete Report?</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              This will permanently delete "{deletingReport?.name}" and its schedule.
              This action cannot be undone.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteReport}
              disabled={deleteReport.isPending}
            >
              {deleteReport.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
